import { common } from '@/features/i18n/common';
import { t } from '@/features/i18n/utils';
import { toProjectType } from '@/features/projects/utils';
import { PROJECT_TYPES, ProjectType } from '@/features/projects/constants';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useAuth } from '@/features/auth';
import { useQuery } from '@tanstack/react-query';
import { Header, HeaderBackButton, HeaderCopyButton, HeaderUserMenu } from '@/ui/components/header';
import { Breadcrumb, BreadcrumbPage } from '@/ui/components/breadcrumb';
import { Icon } from '@/ui/components/icon';
import {
  SparklesIcon,
  ArrowRight02Icon,
  ChartDownIcon,
  ChartUpIcon,
  PlusSignIcon,
  AlertCircleIcon,
  Refresh01Icon,
} from '@hugeicons-pro/core-stroke-rounded';
import {
  MainScrollArea,
  MainHeader,
  MainContainer,
  MainFrames,
  MainHeaderGroup,
  MainHeaderGroupItem,
  MainAlert,
} from '@/ui/components/main';
import { Button } from '@/ui/components/button';
import { H1, H2, H3 } from '@/ui/components/heading';
import { useState } from 'react';
import { Tabs, TabsList, TabsPanel, TabsTab } from '@/ui/components/tabs';
import { useSetAtom } from 'jotai';
import { useGeneratePortfolioLearnings } from '@/features/learnings/mutations';
import { learningsQuery } from '@/features/learnings/queries';
import { createLearningOpenAtom } from '@/features/learnings/store';
import { CreateLearning } from '@/features/learnings/components/create-learning';
import { Badge } from '@/ui/components/badge';
import { Frame, FramePanel } from '@/ui/components/frame';
import { Skeleton } from '@/ui/components/skeleton';
import { AnalysisStatus } from '@/features/workspaces/constants';
import { LearningsActivityOverview } from '@/features/learnings/components/learnings-activity-overview';
import { Alert, AlertAction, AlertDescription, AlertTitle } from '@/ui/components/alert';
import { ClientsIcon, LearningsIcon } from '@/ui/components/empty-icons';
import { Progress, ProgressTrack, ProgressValue } from '@/ui/components/progress';
import { Spinner } from '@/ui/components/spinner';
import {
  organizationTenderFeedbacksQuery,
  orgTenderFeedbackIndexingStatusQuery,
} from '@/features/tender-feedbacks/queries';
import type { LearningInsight, LearningInsightStatement } from '@/features/learnings/types';
import { StandaloneTenderFeedback } from '@/features/tender-feedbacks/types';
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyMedia,
  EmptyTitle,
} from '@/ui/components/empty';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogPopup,
  AlertDialogTitle,
} from '@/ui/components/alert-dialog';
import { Field, FieldLabel } from '@/ui/components/form/field';
import { LanguageSelect } from '@/features/i18n/components/language-select';
import { Tooltip, TooltipPopup, TooltipTrigger } from '@/ui/components/tooltip';
import { GroupSeparator } from '@/ui/components/group';
import { LearningsIndexingAlert } from '@/features/learnings/components/learnings-indexing-alert';

export const Route = createFileRoute('/_authed/_organization/learnings/')({
  component: RouteComponent,
});

function RouteComponent() {
  const [regenOpen, setRegenOpen] = useState(false);
  const auth = useAuth();
  const organizationId = auth.user.organizationId;
  const { data: tenderFeedbacks, isLoading: isLoadingFeedbacks } = useQuery(
    organizationTenderFeedbacksQuery(organizationId),
  );
  const { data, isLoading } = useQuery(learningsQuery(organizationId));

  const indexingStatusQuery = useQuery(orgTenderFeedbackIndexingStatusQuery(organizationId));

  const feedbacksSettled =
    indexingStatusQuery.data?.feedbacks.every(
      (feedback) =>
        feedback.status === AnalysisStatus.DONE || feedback.status === AnalysisStatus.FAILED,
    ) ?? false;
  const feedbackStatusMessage =
    indexingStatusQuery.data && !indexingStatusQuery.isError
      ? t({
          en: `Please wait for tender feedbacks to be analysed (${indexingStatusQuery.data.overallProgressPercentage.toFixed(0)}% complete)`,
          nb: `Vennligst vent til tilbakemeldinger er analysert (${indexingStatusQuery.data.overallProgressPercentage.toFixed(0)}% fullført)`,
        })
      : t({ en: 'Analysis status is unavailable', nb: 'Analysestatus er ikke tilgjengelig' });

  const insights = (data?.insights ?? []).filter((i) => toProjectType(i.projectType));
  const anyProcessing = insights.some((i) => i.status === AnalysisStatus.IN_PROGRESS);

  const avgPercentage =
    insights.reduce((sum, i) => sum + (i.percentage ?? 0), 0) / (insights.length || 1);

  const hasAnyData = insights.length > 0;
  const showContent = anyProcessing ? false : !isLoadingFeedbacks && !isLoading && hasAnyData;

  const noFeedbacks = tenderFeedbacks && tenderFeedbacks.length === 0;

  return (
    <>
      <CreateLearning />
      <Header>
        <HeaderBackButton />
        <Breadcrumb>
          <BreadcrumbPage>
            <Icon icon={SparklesIcon} />
            {t({ en: 'Learnings', nb: 'Erfaringer' })}
          </BreadcrumbPage>
        </Breadcrumb>
        <HeaderUserMenu />
        <HeaderCopyButton />
      </Header>
      <MainScrollArea>
        {indexingStatusQuery.isError && (
          <MainAlert>
            <Alert variant="destructive">
              <Icon icon={AlertCircleIcon} />
              <AlertTitle>
                {t({
                  en: 'Analysis status is unavailable',
                  nb: 'Analysestatus er ikke tilgjengelig',
                })}
              </AlertTitle>
              <AlertDescription>
                {t({
                  en: "We couldn't check whether your past tenders are done being analysed. Creating and regenerating learnings may be unavailable until it loads.",
                  nb: 'Vi kunne ikke sjekke om tidligere anbud er ferdig analysert. Oppretting og regenerering av lærdommer kan være utilgjengelig til statusen er lastet inn.',
                })}
              </AlertDescription>
              <AlertAction>
                <Button variant="secondary" onClick={() => indexingStatusQuery.refetch()}>
                  {t({ en: 'Reload status', nb: 'Last inn status på nytt' })}
                </Button>
              </AlertAction>
            </Alert>
          </MainAlert>
        )}
        <LearningsIndexingAlert />
        {showContent ? (
          <>
            <MainHeader>
              <H1>{t({ en: 'Learnings', nb: 'Erfaringer' })}</H1>
              <MainHeaderGroup>
                <MainHeaderGroupItem
                  render={
                    <Button
                      variant="secondary"
                      kind="with-icon"
                      nativeButton={false}
                      render={
                        <Link to={'/settings/knowledge-base/new-feedback'}>
                          <Icon icon={PlusSignIcon} />
                          {t({
                            en: 'Upload tender feedbacks',
                            nb: 'Last opp anbudstilbakemeldinger',
                          })}
                        </Link>
                      }
                    />
                  }
                />
                <GroupSeparator />
                <Tooltip>
                  <TooltipTrigger
                    delay={0}
                    render={
                      <MainHeaderGroupItem
                        render={
                          <Button
                            kind="with-icon"
                            variant="secondary"
                            disabled={!feedbacksSettled}
                            onClick={() => setRegenOpen(true)}
                          >
                            <Icon icon={Refresh01Icon} />
                            {t({ en: 'Regenerate learnings', nb: 'Opprett læringer på nytt' })}
                          </Button>
                        }
                      />
                    }
                  />
                  {feedbacksSettled ? null : <TooltipPopup>{feedbackStatusMessage}</TooltipPopup>}
                </Tooltip>
                <PortfolioLearningsDialog regenerate open={regenOpen} onOpenChange={setRegenOpen} />
              </MainHeaderGroup>
            </MainHeader>
            <Tabs defaultValue="tender" className="flex flex-col grow min-h-0 pt-2">
              <TabsList className={'pl-x'}>
                <TabsTab value="tender">
                  {t({ en: 'Tender learnings', nb: 'Anbudserfaringer' })}{' '}
                  <Badge size="xs">{t({ en: 'As supplier', nb: 'Som leverandør' })}</Badge>
                </TabsTab>
              </TabsList>
              <MainContainer>
                <TabsPanel value="tender">
                  <LearningsActivityOverview />
                  <PortfolioInsights insights={insights} isLoading={isLoading} />
                  <ClientInsights
                    feedbacksSettled={feedbacksSettled}
                    feedbackStatusMessage={feedbackStatusMessage}
                  />
                </TabsPanel>
              </MainContainer>
            </Tabs>
          </>
        ) : (
          <>
            {(isLoading || isLoadingFeedbacks) && <Spinner kind="inset" />}
            {!isLoading && !isLoadingFeedbacks && (
              <Empty>
                <EmptyMedia>
                  <LearningsIcon />
                </EmptyMedia>
                <EmptyContent>
                  <EmptyTitle>{t({ en: 'Learnings', nb: 'Læringer' })}</EmptyTitle>
                  {anyProcessing ? (
                    <Progress value={+avgPercentage.toFixed(0)} max={100}>
                      <EmptyDescription>
                        {t({ en: 'Finding learnings', nb: 'Finner læringer' })} - <ProgressValue />
                      </EmptyDescription>
                      <ProgressTrack className="md:absolute md:bottom-0 md:rounded-none md:left-0 w-full" />
                    </Progress>
                  ) : (
                    <>
                      <EmptyDescription>
                        {noFeedbacks
                          ? t({
                              en: 'To generate learnings, add tender feedbacks and wait for the files to be analysed.',
                              nb: 'For å generere læringer, legg til anbudstilbakemeldinger og vent på at filene blir analysert.',
                            })
                          : t({
                              en: 'No learnings to display. Generate learnings to get started.',
                              nb: 'Ingen læringer å vise. Generer læringer for å komme i gang.',
                            })}
                      </EmptyDescription>
                      {noFeedbacks ? (
                        <Button
                          kind="with-icon"
                          nativeButton={false}
                          render={
                            <Link to={'/settings/knowledge-base/new-feedback'}>
                              <Icon icon={PlusSignIcon} />
                              {t({
                                en: 'Add tender feedbacks',
                                nb: 'Legg til anbudstilbakemeldinger',
                              })}
                            </Link>
                          }
                        />
                      ) : !feedbacksSettled ? (
                        <Tooltip>
                          <TooltipTrigger
                            delay={0}
                            render={
                              <Button kind="with-icon" disabled>
                                <Icon icon={SparklesIcon} />
                                {t({ en: 'Generate learnings', nb: 'Generer læringer' })}
                              </Button>
                            }
                          />
                          <TooltipPopup>{feedbackStatusMessage}</TooltipPopup>
                        </Tooltip>
                      ) : (
                        <>
                          <Button kind="with-icon" onClick={() => setRegenOpen(true)}>
                            <Icon icon={SparklesIcon} />
                            {t({ en: 'Generate learnings', nb: 'Generer læringer' })}
                          </Button>
                          <PortfolioLearningsDialog open={regenOpen} onOpenChange={setRegenOpen} />
                        </>
                      )}
                    </>
                  )}
                </EmptyContent>
              </Empty>
            )}
          </>
        )}
      </MainScrollArea>
    </>
  );
}

function PortfolioInsights({
  insights,
  isLoading,
}: {
  insights: LearningInsight[];
  isLoading: boolean;
}) {
  const auth = useAuth();
  const organizationId = auth.user.organizationId;
  const { data: tenderFeedbacks } = useQuery(organizationTenderFeedbacksQuery(organizationId));

  return (
    <section>
      <H2 className="mb-3 mt-5">{t({ en: 'Portfolio learnings', nb: 'Porteføljelæring' })}</H2>
      <MainFrames className="@7xl:grid-cols-3">
        {PROJECT_TYPES.map((type) => (
          <ProjectFrame
            key={type}
            type={type}
            item={insights.find((i) => toProjectType(i.projectType) === type)}
            tenderFeedbacks={tenderFeedbacks}
            isLoading={isLoading}
          />
        ))}
      </MainFrames>
    </section>
  );
}

function ProjectFrame({
  type,
  item,
  tenderFeedbacks,
  isLoading,
}: {
  type: ProjectType;
  item: LearningInsight | undefined;
  tenderFeedbacks: StandaloneTenderFeedback[] | undefined;
  isLoading: boolean;
}) {
  const feedbacks = tenderFeedbacks?.filter((fb) => toProjectType(fb.projectType) === type);
  const hasData = item?.statements && item.statements.length > 0;

  return (
    <Frame
      className={hasData ? 'cursor-pointer' : 'cursor-auto'}
      render={
        <Link
          to={'/learnings/portfolio/$projectType'}
          params={{ projectType: type }}
          onClick={(e) => {
            if (!hasData) return e.preventDefault();
          }}
        />
      }
    >
      <FramePanel className="h-38 grid place-items-center">
        {isLoading ? (
          <div className="flex items-center gap-3 flex-col">
            <Skeleton className="w-14 h-8" />
            <Skeleton className="w-32 h-4" />
          </div>
        ) : (
          <StatementCounts statements={item?.statements ?? []} />
        )}
      </FramePanel>
      <div className="flex max-sm:flex-col p-2 pb-1.25 sm:items-center gap-2 min-h-14.75 md:min-h-15.75">
        <div>
          <H3>
            {t({
              en: `${t(common.projectType[type])} learnings`,
              nb: `${t(common.projectType[type])}`,
            })}
          </H3>
          <p className="text-muted mt-0.5 text-sm lowercase first-letter:capitalize">
            {t({
              en: (
                <>
                  {t(common.projectType[type])} learnings identified from{' '}
                  <span className="underline">{feedbacks?.length ?? 0}</span> tender feedbacks.
                </>
              ),
              nb: (
                <>
                  Basert på erfaringer fra{' '}
                  <span className="underline">{feedbacks?.length ?? 0}</span>{' '}
                  anbudstilbakemeldinger.
                </>
              ),
            })}
          </p>
        </div>
        {!hasData ? null : (
          <Icon icon={ArrowRight02Icon} className="invisible group-hover:visible size-6 ml-auto" />
        )}
      </div>
    </Frame>
  );
}

function ClientInsights({
  feedbacksSettled,
  feedbackStatusMessage,
}: {
  feedbacksSettled: boolean;
  feedbackStatusMessage: string;
}) {
  const auth = useAuth();
  const organizationId = auth.user.organizationId;
  const setCreateLearningOpen = useSetAtom(createLearningOpenAtom);

  const { data } = useQuery(learningsQuery(organizationId));
  const { data: tenderFeedbacks } = useQuery(organizationTenderFeedbacksQuery(organizationId));
  const clientsData = data?.insights.filter((i) => i.clientId !== null) ?? [];
  const hasData = clientsData.length > 0;

  return (
    <section>
      <div className="flex justify-between items-center gap-2">
        <H2 className="mb-3 mt-5">{t({ en: 'Client learnings', nb: 'Kundespesifikk læring' })}</H2>
        {!feedbacksSettled ? (
          <Tooltip>
            <TooltipTrigger
              delay={0}
              render={
                <Button variant="secondary" kind="with-icon" disabled>
                  <Icon icon={PlusSignIcon} />
                  {t({ en: 'New client learning', nb: 'Legg til ny kunde' })}
                </Button>
              }
            />
            <TooltipPopup>{feedbackStatusMessage}</TooltipPopup>
          </Tooltip>
        ) : (
          <Button variant="secondary" kind="with-icon" onClick={() => setCreateLearningOpen(true)}>
            <Icon icon={PlusSignIcon} />
            {t({ en: 'New client learning', nb: 'Legg til ny kunde' })}
          </Button>
        )}
      </div>
      <MainFrames
        className="not-data-has-data:@7xl:grid-cols-1 not-data-has-data:@3xl:grid-cols-1 data-has-one:@3xl:grid-cols-2"
        data-has-data={hasData ? '' : undefined}
        data-has-one={clientsData.length === 1 ? '' : undefined}
      >
        {hasData ? (
          clientsData.map((item) => {
            const done = item.status === AnalysisStatus.DONE;

            return (
              <Frame
                key={item.id}
                render={
                  <Link
                    className={!done ? 'cursor-auto' : ''}
                    to={'/learnings/clients/$clientId'}
                    params={{ clientId: item.clientId ?? '' }}
                    onClick={(e) => {
                      if (!done) return e.preventDefault();
                    }}
                  />
                }
              >
                <FramePanel className="h-38 grid place-items-center">
                  {item.status === AnalysisStatus.IN_PROGRESS ? (
                    <Progress value={+item.percentage.toFixed(0)} max={100} className={'mx-auto'}>
                      <p className="text-muted text-sm mb-2 text-center">
                        <Spinner size="xs" className="inline-block mr-2 mb-0.75" />
                        {t({ en: 'Finding learnings', nb: 'Finner læringer' })} - <ProgressValue />
                      </p>
                      <ProgressTrack className={'max-w-50 mx-auto'} />
                    </Progress>
                  ) : item.status === AnalysisStatus.FAILED ? (
                    <p className="text-destructive text-sm mb-2 text-center">
                      {t({
                        en: 'Learnings generation failed. Please retry the analysis.',
                        nb: 'Generering av lærdommer mislyktes. Prøv analysen på nytt.',
                      })}
                    </p>
                  ) : item.status === AnalysisStatus.NOT_STARTED ? (
                    <p className="text-muted text-sm mb-2 text-center">
                      {t({
                        en: 'Search for findings for this client has not started yet.',
                        nb: 'Søket etter funn for denne kunden har ikke startet ennå.',
                      })}
                    </p>
                  ) : done ? (
                    <StatementCounts statements={item.statements} />
                  ) : null}
                </FramePanel>
                <div className="flex max-sm:flex-col p-2 pb-1.25 sm:items-center gap-2 min-h-14.75 md:min-h-15.75">
                  <div>
                    <H3>{item.clientName ?? t({ en: 'Unnamed client', nb: 'Kunde uten navn' })}</H3>
                    <p className="text-muted mt-0.5 text-sm">
                      {t({
                        en: (
                          <>
                            All{' '}
                            {item.clientName ?? t({ en: 'Unnamed client', nb: 'Kunde uten navn' })}{' '}
                            learnings identified from{' '}
                            <span className="underline">
                              {tenderFeedbacks?.filter((fb) => fb.clientName === item.clientName)
                                .length ?? 0}
                            </span>{' '}
                            tender feedbacks.
                          </>
                        ),
                        nb: (
                          <>
                            Alle{' '}
                            {item.clientName ?? t({ en: 'Unnamed client', nb: 'Kunde uten navn' })}{' '}
                            lærdommer identifisert fra{' '}
                            <span className="underline">
                              {tenderFeedbacks?.filter((fb) => fb.clientName === item.clientName)
                                .length ?? 0}
                            </span>{' '}
                            anbudstilbakemeldinger.
                          </>
                        ),
                      })}
                    </p>
                  </div>
                  {!done ? null : (
                    <Icon
                      icon={ArrowRight02Icon}
                      className="invisible group-hover:visible size-6 ml-auto"
                    />
                  )}
                </div>
              </Frame>
            );
          })
        ) : (
          <Frame>
            <FramePanel className="@xl:h-90 grid place-items-center">
              <div className="flex flex-col justify-center p-2 pb-1.25 items-center gap-2 pr-3">
                <ClientsIcon />
                <H2>{t({ en: 'Client specific learnings', nb: 'Klientspesifikk læring' })}</H2>
                <p className="text-muted text-balance mt-0.5 text-sm text-center">
                  {t({
                    en: 'No client learnings to display. Select a client to get started.',
                    nb: 'Ingen klienterfaringer å vise. Velg en klient for å komme i gang.',
                  })}
                </p>
                {!feedbacksSettled ? (
                  <Tooltip>
                    <TooltipTrigger
                      delay={0}
                      render={
                        <Button className="mt-3" variant="secondary" kind="with-icon" disabled>
                          <Icon icon={PlusSignIcon} />
                          {t({ en: 'Choose a client', nb: 'Velg en klient' })}
                        </Button>
                      }
                    />
                    <TooltipPopup>{feedbackStatusMessage}</TooltipPopup>
                  </Tooltip>
                ) : (
                  <Button
                    className="mt-3"
                    variant="secondary"
                    kind="with-icon"
                    onClick={() => setCreateLearningOpen(true)}
                  >
                    <Icon icon={PlusSignIcon} />
                    {t({ en: 'Choose a client', nb: 'Velg en klient' })}
                  </Button>
                )}
              </div>
            </FramePanel>
          </Frame>
        )}
      </MainFrames>
    </section>
  );
}

function StatementCounts({ statements }: { statements: LearningInsightStatement[] }) {
  return (
    <div className="tabular-nums">
      <div className="flex items-center justify-center gap-2">
        <span className="text-[2.4rem] font-medium block">{statements.length}</span>
        <div className="flex flex-col gap-0.5">
          <div className="flex items-center gap-1 text-sm leading-tight font-medium">
            <Icon icon={ChartUpIcon} className="size-4 text-green-11" />{' '}
            {statements.filter((s) => s.changeDirection === 'improvement').length}
            <span className="sr-only">{t({ en: 'Improving', nb: 'Forbedres' })}</span>
          </div>
          <div className="flex items-center gap-1 text-sm leading-tight font-medium">
            <Icon icon={ChartDownIcon} className="size-4 text-red-11" />{' '}
            {statements.filter((s) => s.changeDirection === 'worsening').length}
            <span className="sr-only"> {t({ en: 'Worsening', nb: 'Forverres' })}</span>
          </div>
        </div>
      </div>
      <p className="text-sm">{t({ en: 'Learnings found', nb: 'Lærdommer funnet' })}</p>
    </div>
  );
}

function PortfolioLearningsDialog({
  open,
  onOpenChange,
  regenerate,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  regenerate?: boolean;
}) {
  const [language, setLanguage] = useState('en-US');
  const generate = useGeneratePortfolioLearnings();
  const label = regenerate
    ? t(common.reRunAnalysis)
    : t({ en: 'Generate learnings', nb: 'Generer læringer' });

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogPopup>
        <AlertDialogTitle>{regenerate ? `${label}?` : label}</AlertDialogTitle>
        <AlertDialogDescription>
          {regenerate
            ? t(common.reRunAnalysisDescription)
            : t({
                en: 'Choose the output language for the generated learnings.',
                nb: 'Velg utdataspråket for de genererte læringene.',
              })}
        </AlertDialogDescription>
        <Field className="mt-2 mb-2">
          <FieldLabel>{t({ en: 'Output language', nb: 'Utdataspråk' })}</FieldLabel>
          <LanguageSelect value={language} onValueChange={setLanguage} />
        </Field>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={generate.isPending} />
          <Button
            pending={generate.isPending}
            onClick={() => generate.mutate({ language }, { onSuccess: () => onOpenChange(false) })}
          >
            {label}
          </Button>
        </AlertDialogFooter>
      </AlertDialogPopup>
    </AlertDialog>
  );
}
